import React, { useState, useEffect, useRef } from 'react';
import CommonModal from '../modals/CommonModal';
import '../../styles/staffPin.css';

const PIN_LENGTH = 4;

/**
 * PIN entry for the staff-identity flows: the idle screen lock, Switch User, and later manager
 * authorization. Four digit boxes, dark sheet, primary-red accent - deliberately identical in
 * markup, class names and palette to the local branch's ValidatePinModal, so the two stay easy to
 * merge and staff see the same screen on either build.
 *
 * Still a separate component rather than a rework of ValidatePinModal, and that is not
 * cosmetic: Timesheets and EOB Revenue use that modal with a free-length PIN field, and 39
 * employees on the estate have a PIN that is not 4 digits (35 with three, one with five, three
 * with a single character). Making the shared modal 4-digit-only would stop those people clocking
 * in. They are in restaurants 211, 224, 237, 244, 266, 276, 283, 309 and 320 - none of them the
 * restaurant testing this - and each needs a 4-digit PIN set in the admin Staff screen before
 * staff PIN login is switched on for them.
 *
 * Additions over the local copy, neither of which touches the visual language: an inline error
 * line, because a toast on a locked screen can render behind the modal; and a busy state, so a
 * slow validate-pin round trip cannot be double-submitted.
 */
const StaffPinModal = ({
  isOpen,
  onCancel,
  onConfirm,
  allowClose = true,
  title = 'Staff Login',
  subtitle = 'Enter your 4-digit PIN to continue',
  error = '',
  busy = false,
  onBack = null,
}) => {
  const [digits, setDigits] = useState(Array(PIN_LENGTH).fill(''));
  const inputRefs = useRef([]);

  useEffect(() => {
    if (isOpen) {
      setDigits(Array(PIN_LENGTH).fill(''));
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    }
  }, [isOpen]);

  // Clear the boxes on a rejected PIN so the next attempt starts from an empty row rather than
  // needing four backspaces.
  useEffect(() => {
    if (error) {
      setDigits(Array(PIN_LENGTH).fill(''));
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    }
  }, [error]);

  const submit = (pin) => {
    if (pin.length === PIN_LENGTH && !busy) {
      onConfirm(pin);
    }
  };

  const handleChange = (index, rawValue) => {
    const value = rawValue.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = value;
    setDigits(next);

    if (value && index < PIN_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    if (value && index === PIN_LENGTH - 1) {
      submit(next.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      submit(digits.join(''));
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, PIN_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(PIN_LENGTH).fill('');
    pasted.split('').forEach((char, i) => { next[i] = char; });
    setDigits(next);
    if (pasted.length === PIN_LENGTH) {
      submit(pasted);
    } else {
      inputRefs.current[pasted.length]?.focus();
    }
  };

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={allowClose ? onCancel : () => {}}
      elId="staffPinModal"
      closeOnBackdropClick={allowClose}
      closeOnEscape={allowClose}
    >
      <div className="pinModalBody">
        <div className="pinModalIcon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 10-8 0v2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {allowClose && (
          <button type="button" className="pinModalClose" onClick={onCancel} aria-label="Close">
            &times;
          </button>
        )}

        <h1 className="pinModalTitle">{title}</h1>
        <p className="pinModalSubtitle">{subtitle}</p>

        <div className="pinDigitsRow" onPaste={handlePaste}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="password"
              inputMode="numeric"
              maxLength={1}
              className="pinDigitBox"
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
            />
          ))}
        </div>

        {/* Reserves its line whether or not there is an error, so nothing under the user's finger
            shifts the moment a wrong PIN comes back. */}
        <p className="pinModalError" role="alert">{error || ' '}</p>

        <button className="pinValidateBtn" onClick={() => submit(digits.join(''))} disabled={busy}>
          {busy ? 'Checking…' : 'Validate'}
        </button>

        {allowClose ? (
          <button className="pinBackLink" onClick={onCancel}>Close</button>
        ) : (
          onBack && <button className="pinBackLink" onClick={onBack}>Back to Dashboard</button>
        )}
      </div>
    </CommonModal>
  );
};

export default StaffPinModal;
